import moment from 'moment-timezone';
import {
  booleanToYesNo,
  isIncidentYesNoKeyPointer,
} from 'src/modules/incident/incidentFormAnswerCoerce';

const INCIDENT_RELATION_TIPOS = new Set([
  'perfilEmpleado',
  'usuario',
  'location',
  'thirdParty',
  'asset',
  'project',
]);

/** Texto visible para prefill de trabajador (perfilEmpleado); nunca usar el id como label. */
function buildPerfilEmpleadoPrefillLabel(raw: Record<string, any> | null | undefined): string {
  if (raw == null || typeof raw !== 'object') return '';
  if (raw.label != null && String(raw.label).trim() !== '') return String(raw.label).trim();
  if (raw.fullName != null && String(raw.fullName).trim() !== '') return String(raw.fullName).trim();
  const nombre = raw.nombre != null ? String(raw.nombre).trim() : '';
  const apellidos = raw.apellidos != null ? String(raw.apellidos).trim() : '';
  const composed = [nombre, apellidos].filter(Boolean).join(' ').trim();
  if (composed) return composed;
  if (raw.email != null && String(raw.email).trim() !== '') return String(raw.email).trim();
  return '';
}

/** Texto visible para prefill de usuario (modelo `user`); nunca usar el id como label. */
function buildUserPrefillLabel(raw: Record<string, any> | null | undefined): string {
  if (raw == null || typeof raw !== 'object') return '';
  if (raw.label != null && String(raw.label).trim() !== '') return String(raw.label).trim();
  if (raw.fullName != null && String(raw.fullName).trim() !== '') return String(raw.fullName).trim();
  const first = raw.firstName != null ? String(raw.firstName).trim() : '';
  const last = raw.lastName != null ? String(raw.lastName).trim() : '';
  const composed = [first, last].filter(Boolean).join(' ').trim();
  if (composed) return composed;
  if (raw.email != null && String(raw.email).trim() !== '') return String(raw.email).trim();
  return '';
}

function buildLocationPrefillLabel(raw: Record<string, any> | null | undefined): string {
  if (raw == null || typeof raw !== 'object') return '';
  if (raw.label != null && String(raw.label).trim() !== '') return String(raw.label).trim();
  if (raw.nombre != null && String(raw.nombre).trim() !== '') return String(raw.nombre).trim();
  if (raw.descripcion != null && String(raw.descripcion).trim() !== '') return String(raw.descripcion).trim();
  return '';
}

function buildThirdPartyPrefillLabel(raw: Record<string, any> | null | undefined): string {
  if (raw == null || typeof raw !== 'object') return '';
  if (raw.label != null && String(raw.label).trim() !== '') return String(raw.label).trim();
  if (raw.nombre != null && String(raw.nombre).trim() !== '') return String(raw.nombre).trim();
  return '';
}

function buildAssetPrefillLabel(raw: Record<string, any> | null | undefined): string {
  if (raw == null || typeof raw !== 'object') return '';
  if (raw.label != null && String(raw.label).trim() !== '') return String(raw.label).trim();
  if (raw.nombre != null && String(raw.nombre).trim() !== '') return String(raw.nombre).trim();
  if (raw.codigo != null && String(raw.codigo).trim() !== '') return String(raw.codigo).trim();
  return '';
}

function buildProjectPrefillLabel(raw: Record<string, any> | null | undefined): string {
  if (raw == null || typeof raw !== 'object') return '';
  if (raw.label != null && String(raw.label).trim() !== '') return String(raw.label).trim();
  const name = raw.name != null ? String(raw.name).trim() : '';
  const code = raw.code != null ? String(raw.code).trim() : '';
  if (code && name) return `${code} — ${name}`;
  return name || code;
}

function labelForRelationTipo(raw: Record<string, any>, tipo: string): string {
  switch (tipo) {
    case 'perfilEmpleado':
      return buildPerfilEmpleadoPrefillLabel(raw);
    case 'usuario':
      return buildUserPrefillLabel(raw);
    case 'location':
      return buildLocationPrefillLabel(raw);
    case 'thirdParty':
      return buildThirdPartyPrefillLabel(raw);
    case 'asset':
      return buildAssetPrefillLabel(raw);
    case 'project':
      return buildProjectPrefillLabel(raw);
    default:
      return '';
  }
}

function normalizeOneRelationPrefill(raw: any, tipo: string): any {
  if (raw === undefined || raw === null) return null;
  if (typeof raw !== 'object') {
    return {id: raw, label: ''};
  }
  if (Array.isArray(raw)) {
    return null;
  }
  const id = raw.id ?? raw._id;
  if (id == null || id === '') {
    return null;
  }
  const built = labelForRelationTipo(raw, tipo);
  const label =
    built ||
    (raw.label != null && String(raw.label).trim() !== '' ? String(raw.label).trim() : '');
  return {id, label};
}

function normalizeIncidentRelationPrefillValue(
  prefillValue: any,
  isMultiple: boolean | undefined,
  tipo: string,
): any {
  if (isMultiple) {
    const parts = Array.isArray(prefillValue) ? prefillValue : prefillValue != null ? [prefillValue] : [];
    return parts.map((p) => normalizeOneRelationPrefill(p, tipo)).filter((x) => x != null);
  }
  const one = Array.isArray(prefillValue) ? prefillValue[0] : prefillValue;
  return normalizeOneRelationPrefill(one, tipo);
}

const INCIDENT_KEYPOINTER_ALIASES: Record<string, string[]> = {
  incidentDateTime: ['horaSiniestro'],
  horaSiniestro: ['incidentDateTime'],
};

function isEmptyIncidentLookupValue(value: any): boolean {
  return value === undefined || value === null || value === '';
}

/** Keys extra a consultar cuando el keyPointer no está en el documento Incident. */
export function aliasIncidentLookupKeys(keyPointer: string): string[] {
  const base = String(keyPointer || '').replace(/^incident_/, '');
  return INCIDENT_KEYPOINTER_ALIASES[base] ? [...INCIDENT_KEYPOINTER_ALIASES[base]] : [];
}

export function lookupIncidentValueWithAliases(incident: Record<string, any> | null | undefined, keyPointer: string): any {
  if (!incident || !keyPointer) return undefined;
  const kp = String(keyPointer);
  const candidates = [kp];
  if (kp.startsWith('incident_')) {
    candidates.push(kp.replace(/^incident_/, ''));
  }
  for (const c of [...candidates]) {
    for (const alias of aliasIncidentLookupKeys(c)) {
      if (!candidates.includes(alias)) candidates.push(alias);
    }
  }
  for (const key of candidates) {
    const value = incident[key];
    if (!isEmptyIncidentLookupValue(value)) return value;
  }
  return undefined;
}

/** Normaliza hora de incidente a `HH:mm` (acepta HH:mm, HH:mm:ss e ISO). */
export function parseIncidentPrefillTimeToHHmm(raw: any): string | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    const match = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (match) {
      const hh = Number(match[1]);
      const mm = Number(match[2]);
      if (hh > 23 || mm > 59) return null;
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    }
  }
  const strict = moment(raw, [moment.ISO_8601, 'HH:mm:ss', 'HH:mm', 'H:mm:ss', 'H:mm'], true);
  if (strict.isValid()) return strict.format('HH:mm');
  const loose = moment(raw);
  return loose.isValid() ? loose.format('HH:mm') : null;
}

function flattenAlternativasCheckbox(nodes: any[] = [], parentPath = ''): {id: any; label: string}[] {
  const flat: {id: any; label: string}[] = [];
  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    if (!node) return;
    const nodeLabel = node.label != null ? String(node.label) : '';
    const pathLabel = parentPath ? `${parentPath} > ${nodeLabel}` : nodeLabel;
    flat.push({id: node.id, label: pathLabel});
    if (Array.isArray(node.children) && node.children.length) {
      flat.push(...flattenAlternativasCheckbox(node.children, pathLabel));
    }
  });
  return flat;
}

function selectedMultiCheckboxLabels(prefillValue: any): string[] {
  const parts = Array.isArray(prefillValue) ? prefillValue : prefillValue != null && prefillValue !== '' ? [prefillValue] : [];
  return parts
    .map((part) => {
      if (part == null || part === '') return '';
      if (typeof part === 'object') {
        return String(part.pathLabel || part.fullLabel || part.label || part.id || '').trim();
      }
      return String(part).trim();
    })
    .filter(Boolean);
}

function applyMultiCheckboxPrefill(pregunta: any, prefillValue: any): void {
  const selected = selectedMultiCheckboxLabels(prefillValue);
  const selectedSet = new Set(selected);
  const alts = flattenAlternativasCheckbox(pregunta?.alternativasCheckbox);
  const matchesAlt = (alt: {id: any; label: string}) =>
    selectedSet.has(String(alt.id)) || selectedSet.has(String(alt.label));

  if (!alts.length) {
    pregunta.multiCheckbox = selected.map((label) => ({id: label, label, value: true}));
    return;
  }

  const next = alts.map((alt) => ({
    id: alt.id,
    label: alt.label,
    value: matchesAlt(alt),
  }));

  const unmatched = selected.filter((s) => !alts.some((alt) => String(alt.id) === s || String(alt.label) === s));
  if (unmatched.length && pregunta.allowOthersOption) {
    let others = next.find((opt) => opt.id === 'others' || opt.label === 'Otros');
    if (!others) {
      others = {id: 'others', label: 'Otros', value: true};
      next.push(others);
    } else {
      others.value = true;
    }
    pregunta.multiCheckboxOthers = unmatched.join(', ');
  }

  pregunta.multiCheckbox = next;
}

/** Normaliza fecha de prellenado desde incidente para controles tipo `date`. */
export function applyIncidentPrefillToPreguntaField(pregunta: any, prefillValue: any): void {
  if (prefillValue === undefined || prefillValue === null) return;
  const tipo = pregunta?.tipoRespuesta;
  if (isIncidentYesNoKeyPointer(pregunta?.keyPointer) || tipo === 'yesNo') {
    const yesNo = booleanToYesNo(prefillValue);
    pregunta.yesNo = yesNo != null ? yesNo : prefillValue;
    if (tipo === 'checkbox') {
      pregunta.checkbox = prefillValue === true;
    }
    return;
  }
  if (tipo === 'date') {
    const m = moment(prefillValue);
    pregunta.date = m.isValid() ? m.toISOString() : prefillValue;
    return;
  }
  if (tipo === 'time') {
    const parsed = parseIncidentPrefillTimeToHHmm(prefillValue);
    pregunta.time = parsed != null ? parsed : prefillValue;
    return;
  }
  if (tipo === 'multiCheckbox') {
    applyMultiCheckboxPrefill(pregunta, prefillValue);
    return;
  }
  if (tipo && INCIDENT_RELATION_TIPOS.has(tipo)) {
    const normalized = normalizeIncidentRelationPrefillValue(prefillValue, pregunta?.isMultiple, tipo);
    if (normalized == null) return;
    if (Array.isArray(normalized) && normalized.length === 0 && !pregunta?.isMultiple) return;
    pregunta[tipo] = normalized;
    return;
  }
  pregunta[tipo] = prefillValue;
}
